/**
 * Tabs - сучасний компонент табів на базі UIManager
 *
 * Підтримує:
 * - Інтеграцію з CrudResourceManager та FormContext
 * - Умовний рендеринг табів через depFn
 * - Автоматичну фільтрацію табів за умовами
 */

import { observer } from 'mobx-react-lite'
import * as React from 'react'

import { useProfiler } from '@/Core/Utils/profilerHelpers'
import type { TabItem, TabsProps } from './Tabs.types'
import { createTabsContext, TabsContextProvider } from './TabsContextProvider'

/**
 * Компонент Tabs
 */
export const Tabs: React.FC<TabsProps> = ({
    depFn,
    depStore = {},
    items = [],
    tabsContext: externalTabsContext = null,
    ...tabsProps
}) => {
    /** Інтеграція з профайлером в dev режимі */
    const { addEvent, setStatus } = useProfiler('Tabs', 'component', '/Core/UI/Tabs/Tabs.tsx', [tabsProps, depStore])

    React.useEffect(() => {
        setStatus('active')
        addEvent('initialized', { props: tabsProps, depStore })
    }, [])

    /** Створюємо tabsContext */
    let tabsContext = externalTabsContext
    if( tabsContext )
        tabsContext.mergeProps({ ...tabsProps, items })
    else
        tabsContext = createTabsContext({ ...tabsProps, items })

    /** Компонент з логікою обробки табів */
    const ObserverComponent = observer(() => {
        addEvent('render', { timestamp: Date.now() })

        /** Фільтруємо таби за depFn */
        const filteredItems: TabItem[] = items.filter((item) => {
            if (typeof item.depFn !== 'function') {
                return true
            }

            return item.depFn({})
        })

        /** Оновлюємо пропси з відфільтрованими табами */
        tabsContext.mergeProps({ items: filteredItems })

        /** Рендеримо таби */
        return tabsContext.render()
    })

    return (
        <TabsContextProvider value={tabsContext}>
            <ObserverComponent />
        </TabsContextProvider>
    )
}

export default Tabs
